import { useTabs } from '../../context/TabsContext';
import { useTabClick } from './useTabClick';
import { useTabSelection } from './useTabSelection';
import { useTabOpenActions } from './useTabOpenActions';
import { useTabSync } from './useTabSync';

/**
 * Hook that manages tab integration with sprint/template navigation.
 * Handles tab clicks, updates, and synchronization with selected items.
 */
export function useTabIntegration({
  sprints,
  selectedSprint,
  selectedTemplate,
  selectedSprintId,
  selectedTemplateId,
  setSelectedSprintId,
  setSelectedTemplateId,
  setCurrentView,
  setLastSelectedCampaignId,
  loadFullSprint,
  loadFullTemplate,
  handleSelectSprint,
  handleSelectTemplate,
  handleSelectView,
  setIsLoadingItem,
  setPreferredDetailView,
  setPendingMissionFocus,
  clearActiveView,
  activeView,
  currentView,
  preferences,
  isInitialStartupComplete,
  VIEWS,
}) {
  const {
    tabs,
    activeTab,
    openTab,
    updateTab,
    consumeSkipNextActiveTabEffect,
    isLoaded: tabsLoaded,
  } = useTabs();

  const { handleTabClick } = useTabClick({
    sprints,
    selectedSprintId,
    selectedTemplateId,
    setSelectedSprintId,
    setSelectedTemplateId,
    setCurrentView,
    setLastSelectedCampaignId,
    loadFullSprint,
    loadFullTemplate,
    handleSelectView,
    setIsLoadingItem,
    setPreferredDetailView,
    setPendingMissionFocus,
    clearActiveView,
    activeView,
    currentView,
    VIEWS,
  });

  const {
    handleSelectSprintAndUpdateTab,
    handleSelectTemplateAndUpdateTab,
    handleSelectViewAndUpdateTab,
  } = useTabSelection({
    activeTab,
    updateTab,
    openTab,
    handleSelectSprint,
    handleSelectTemplate,
    handleSelectView,
    preferences,
  });

  const { handleOpenInNewTab, handleNewTab } = useTabOpenActions({
    openTab,
    handleSelectSprint,
    handleSelectTemplate,
    handleSelectView,
    clearActiveView,
    setSelectedSprintId,
    setSelectedTemplateId,
    setCurrentView,
    setIsLoadingItem,
    preferences,
    VIEWS,
  });

  const { handleViewModeChange, syncTabToContent } = useTabSync({
    activeTab,
    tabs,
    tabsLoaded,
    updateTab,
    openTab,
    selectedSprint,
    selectedTemplate,
    activeView,
    isInitialStartupComplete,
    handleTabClick,
    consumeSkipNextActiveTabEffect,
    preferences,
  });

  return {
    handleTabClick,
    handleSelectSprintAndUpdateTab,
    handleSelectTemplateAndUpdateTab,
    handleSelectViewAndUpdateTab,
    handleOpenInNewTab,
    handleNewTab,
    handleViewModeChange,
    syncTabToContent,
  };
}
